import { extrairMensagensErro } from '@/shared/http/extrairMensagensErro';

export const HTTP_ERROR_SCOPE = {
    PAGE: 'page',
    LOCAL: 'local',
    SILENT: 'silent',
};

/**
 * @param {import('axios').AxiosRequestConfig | undefined} config
 * @returns {'page' | 'local' | 'silent'}
 */
export function resolveHttpErrorScope(config = {}) {
    if (config.sebraeSilentError) {
        return HTTP_ERROR_SCOPE.SILENT;
    }

    if (config.sebraeErrorScope) {
        return config.sebraeErrorScope;
    }

    return HTTP_ERROR_SCOPE.PAGE;
}

/**
 * @typedef {Object} HttpErrorClassification
 * @property {'page' | 'local' | 'silent'} scope
 * @property {'show-feedback' | 'local' | 'logout'} action
 * @property {string | null} variant
 * @property {number | null} status
 * @property {string[]} messages
 * @property {boolean} shouldLogout
 */

/**
 * @param {import('axios').AxiosError} error
 * @returns {HttpErrorClassification}
 */
export function classifyHttpError(error) {
    const config = error?.config ?? {};
    const scope = resolveHttpErrorScope(config);
    const messages = extrairMensagensErro(error);

    if (!error?.response) {
        if (error?.request) {
            return {
                scope,
                action: scope === HTTP_ERROR_SCOPE.SILENT ? 'local' : 'show-feedback',
                variant: 'offline',
                status: null,
                messages,
                shouldLogout: false,
            };
        }

        return {
            scope,
            action: 'local',
            variant: null,
            status: null,
            messages,
            shouldLogout: false,
        };
    }

    const { status, data } = error.response;

    if (status === 401) {
        return {
            scope,
            action: 'logout',
            variant: 'session-expired',
            status,
            messages,
            shouldLogout: true,
        };
    }

    if (status === 403) {
        return {
            scope,
            action: scope === HTTP_ERROR_SCOPE.SILENT ? 'local' : 'show-feedback',
            variant: 'forbidden',
            status,
            messages,
            shouldLogout: false,
        };
    }

    if (status === 404) {
        const isBlob = config.responseType === 'blob';

        return {
            scope,
            action:
                isBlob || scope === HTTP_ERROR_SCOPE.LOCAL || scope === HTTP_ERROR_SCOPE.SILENT ? 'local' : 'show-feedback',
            variant: 'not-found',
            status,
            messages,
            shouldLogout: false,
        };
    }

    if (status === 400 || status === 422) {
        if (data?.exception && scope === HTTP_ERROR_SCOPE.PAGE) {
            return {
                scope,
                action: 'show-feedback',
                variant: 'server-error',
                status,
                messages,
                shouldLogout: false,
            };
        }

        return {
            scope,
            action: 'local',
            variant: null,
            status,
            messages,
            shouldLogout: false,
        };
    }

    if (status === 409) {
        return {
            scope,
            action: 'local',
            variant: null,
            status,
            messages,
            shouldLogout: false,
        };
    }

    if (status === 429) {
        return {
            scope,
            action: scope === HTTP_ERROR_SCOPE.SILENT ? 'local' : 'show-feedback',
            variant: 'rate-limit',
            status,
            messages,
            shouldLogout: false,
        };
    }

    if (status === 502 || status === 503) {
        return {
            scope,
            action: scope === HTTP_ERROR_SCOPE.SILENT ? 'local' : 'show-feedback',
            variant: 'unavailable',
            status,
            messages,
            shouldLogout: false,
        };
    }

    if (status >= 500) {
        return {
            scope,
            action: scope === HTTP_ERROR_SCOPE.SILENT ? 'local' : 'show-feedback',
            variant: 'server-error',
            status,
            messages,
            shouldLogout: false,
        };
    }

    return {
        scope,
        action: 'local',
        variant: null,
        status,
        messages,
        shouldLogout: false,
    };
}
